import { FC, useState } from 'react';
import { ConnectDragSource } from 'react-dnd';
import { createFragmentContainer, graphql } from 'react-relay';
import { DesignProjectTileBgImage } from './DesignProjectTileBgImage';
import { DesignProjectTileDetails } from './DesignProjectTileDetails';
import { DesignProjectTileStatusBadge } from './DesignProjectTileStatusBadge';
import { DesignProjectTileEditOverlay } from './DesignProjectTileEditOverlay';
import { DeleteProjectConfirmationModal } from './DeleteProjectConfirmationModal';

import styles from './EditDesignProjectTile.scss';

import { EditDesignProjectTile_designProject as DesignProject } from './__generated__/EditDesignProjectTile_designProject.graphql';

export type Props = {
    adminHost: string;
    designProject: DesignProject | null;
    urlLabel: string;
    isAdminMode: boolean;
    connectDragSource?: ConnectDragSource;
    setDragTargetProjectId?: (id?: string) => void; // needed by EditDesignProjectTileDraggable
    unsetDragTargetProjectId?: (id?: string) => void; // needed by EditDesignProjectTileDraggable
};

export const EditDesignProjectTileComponent: FC<Props> = ({
    adminHost,
    designProject,
    urlLabel,
    isAdminMode,
    connectDragSource,
}) => {
    const [isHovering, setIsHovering] = useState(false);
    const [showDeleteProjectConfirmation, setShowDeleteProjectConfirmation] = useState(false);

    if (!designProject) {
        return null;
    }

    const containerClass = connectDragSource
        ? styles.innerContainerDraggable
        : styles.innerContainer;

    const content = (
        <div
            className={containerClass}
            data-tn="edit-design-project-tile"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
        >
            <DesignProjectTileEditOverlay
                isVisible={isHovering}
                editPath={
                    (isAdminMode
                        ? `${adminHost}${designProject?.internalEditUrl || ''}`
                        : designProject?.editUrl) || ''
                }
                isAdminMode={isAdminMode}
                onDelete={() => setShowDeleteProjectConfirmation(true)}
            />
            <DesignProjectTileBgImage coverPhotoPath={designProject?.tradeCoverPhoto?.path || ''} />
            <div className={styles.detailsWrapper}>
                <DesignProjectTileDetails
                    addressDisplayString={designProject?.addressDisplayString}
                    name={designProject?.name}
                />
                <div className={styles.statusWrapper}>
                    <DesignProjectTileStatusBadge designProject={designProject} />
                </div>
            </div>
            <DeleteProjectConfirmationModal
                isOpen={showDeleteProjectConfirmation}
                designProject={designProject}
                urlLabel={urlLabel}
                onClose={() => setShowDeleteProjectConfirmation(false)}
            />
        </div>
    );

    return connectDragSource ? (
        connectDragSource(content)
    ) : (
        <div className={styles.containerEditable}>{content}</div>
    );
};

export const EditDesignProjectTile = createFragmentContainer(EditDesignProjectTileComponent, {
    designProject: graphql`
        fragment EditDesignProjectTile_designProject on designProject {
            ...DesignProjectTileStatusBadge_designProject
            ...DeleteProjectConfirmationModal_designProject
            serviceId
            addressDisplayString
            internalEditUrl
            editUrl
            name
            rootId
            tradeCoverPhoto {
                path
            }
        }
    `,
});
